/** The Leadsales mark, drawn on a 24 px grid. */
const LEADSALES_ICON_PATH =
  'M21.002 4.0456C22.3168 5.51873 23.3306 7.13227 23.6639 9.06004C23.9208 10.5462 24.1381 12.0291 23.8923 13.6161C23.652 15.1671 23.1569 16.5357 22.4963 17.9044C22.3667 18.1724 22.3576 18.5645 22.4386 18.8585C22.685 19.7543 23.0184 20.6253 23.2882 21.5152C23.6129 22.5867 22.9799 23.459 21.8704 23.4626C21.5394 23.4635 21.1981 23.3897 20.8789 23.2925C19.9372 23.0055 19.0044 22.6898 18.1057 22.3974C13.8576 24.5208 9.75882 24.6243 5.76017 22.1646C2.61571 20.2305 0.703601 17.3469 0.145447 13.7011C-0.272195 10.9732 0.201058 8.31825 1.67569 5.9125C3.57371 2.8165 6.27216 0.790709 9.83222 0.203136C14.1801 -0.514572 17.9689 0.647129 21.002 4.0456ZM5.16561 7.45185C3.45422 10.0413 3.25977 12.8024 4.61941 15.5932C5.87867 18.1777 7.98526 19.7297 10.8001 20.1672C12.4164 20.4184 14.0465 20.2538 15.5183 19.4659C16.5165 18.9314 17.4437 18.3094 18.6715 19.0102C18.4228 17.9886 18.4968 17.1619 19.0181 16.3101C21.743 11.8583 19.8808 6.26917 14.9726 4.32086C11.4 2.90285 7.42184 4.03777 5.16561 7.45185Z';

const OUTLINE_STROKE = {
  stroke: 'var(--ls-border-neutral-default)',
  strokeWidth: 1.5,
};

const ARROW_STROKE = {
  fill: 'none',
  stroke: 'var(--ls-accent-primary-default)',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

type tChatBubbleProps = {
  x: number;
  y: number;
  width: number;
  color: string;
};

function ChatBubble(props: tChatBubbleProps) {
  const { x, y, width, color } = props;
  return <rect x={x} y={y} width={width} height="7" rx="2.5" fill={color} />;
}

export type tWhatsAppConnectionWizardPathDrawingProps = {
  /** Whether the path keeps the WhatsApp Business app working beside Leadsales. */
  keepsApp: boolean;
};

/**
 * The drawing beside a step 1 answer: the phone's app, the arrows between it
 * and Leadsales, and Leadsales. Keeping the app draws a phone with
 * conversations and one arrow each way; moving everything to Leadsales draws
 * an empty dashed phone and one arrow. Decorative, and shown from md up only.
 *
 * @param {tWhatsAppConnectionWizardPathDrawingProps} props - Whether the path
 * keeps the app.
 * @returns {JSX.Element} The drawing.
 */
export default function WhatsAppConnectionWizardPathDrawing(
  props: tWhatsAppConnectionWizardPathDrawingProps,
) {
  const { keepsApp } = props;

  return (
    // At the answer row's height, so the drawing does not make the card taller.
    <svg
      viewBox="0 0 112 60"
      width="52"
      height="28"
      aria-hidden="true"
      focusable="false"
      className="hidden shrink-0 md:block"
    >
      {keepsApp ? (
        <>
          <rect
            x="6"
            y="6"
            width="30"
            height="48"
            rx="6"
            fill="var(--ls-surface-background)"
            {...OUTLINE_STROKE}
          />
          <ChatBubble
            x={11}
            y={15}
            width={16}
            color="var(--ls-border-neutral-light)"
          />
          <ChatBubble
            x={17}
            y={25}
            width={14}
            color="var(--ls-accent-primary-light)"
          />
          <ChatBubble
            x={11}
            y={35}
            width={12}
            color="var(--ls-border-neutral-light)"
          />
          <path d="M42 25 H60 M56 21 L60 25 L56 29" {...ARROW_STROKE} />
          <path d="M60 37 H42 M46 33 L42 37 L46 41" {...ARROW_STROKE} />
        </>
      ) : (
        <>
          {/* The app is left empty: a lighter dashed outline with no
              conversations in it. */}
          <rect
            x="6"
            y="6"
            width="30"
            height="48"
            rx="6"
            fill="none"
            stroke="var(--ls-border-neutral-light)"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <path d="M42 30 H60 M56 26 L60 30 L56 34" {...ARROW_STROKE} />
        </>
      )}
      <rect
        x="66"
        y="8"
        width="40"
        height="44"
        rx="6"
        fill="var(--ls-surface-background)"
        {...OUTLINE_STROKE}
      />
      <rect
        x="71"
        y="13"
        width="14"
        height="14"
        rx="3.5"
        fill="var(--ls-accent-primary-default)"
      />
      <path
        d={LEADSALES_ICON_PATH}
        fill="var(--ls-neutral-100)"
        fillRule="evenodd"
        transform="translate(73 15) scale(0.4167)"
      />
      <ChatBubble
        x={71}
        y={32}
        width={30}
        color="var(--ls-border-neutral-light)"
      />
      <ChatBubble
        x={71}
        y={42}
        width={22}
        color="var(--ls-border-neutral-light)"
      />
    </svg>
  );
}
